import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {

  Download,

  Settings,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  FileText,
  Search,
  RefreshCw,
  ArrowLeft,
  Eye,

  Activity,
  Calendar,
  DollarSign
} from 'lucide-react';
import { formatCurrency, formatDate } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const RentasIntegration = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();

  const [selectedTab, setSelectedTab] = useState('files');
  const [selectedFile, setSelectedFile] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Mock data for demonstration
  const mockRentasFiles = [
    {
      id: 1,
      filename: 'RENTAS_INFRA_20241220.xml',
      bondName: 'Malaysia Infrastructure Bond 2024',
      paymentDate: '2024-12-20',
      generationDate: '2024-12-19T14:30:00',
      totalAmount: 12500000,
      investorCount: 245,
      status: 'Generated',
      downloadUrl: '#',
      fileSize: '2.3 MB'
    },
    {
      id: 2,
      filename: 'RENTAS_GREEN_20241222.xml',
      bondName: 'Green Sukuk Series 3',
      paymentDate: '2024-12-22',
      generationDate: '2024-12-21T09:15:00',
      totalAmount: 7125000,
      investorCount: 156,
      status: 'Submitted',
      submissionDate: '2024-12-21T10:30:00',
      downloadUrl: '#',
      fileSize: '1.8 MB'
    },
    {
      id: 3,
      filename: 'RENTAS_TECH_20241225.xml',
      bondName: 'Tech Development Bond',
      paymentDate: '2024-12-25',
      generationDate: '2024-12-24T16:45:00',
      totalAmount: 4500000,
      investorCount: 89,
      status: 'Confirmed',
      submissionDate: '2024-12-24T17:00:00',
      confirmationDate: '2024-12-25T08:00:00',
      downloadUrl: '#',
      fileSize: '1.2 MB',
      confirmationFile: 'RENTAS_CONFIRMATION_20241225.pdf'
    },
    {
      id: 4,
      filename: 'RENTAS_CORP_20241215.xml',
      bondName: 'Corporate Bond 2024-A',
      paymentDate: '2024-12-15',
      generationDate: '2024-12-14T11:20:00',
      totalAmount: 8750000,
      investorCount: 198,
      status: 'Error',
      errorMessage: 'Invalid bank code format in investor data',
      downloadUrl: '#',
      fileSize: '2.1 MB'
    }
  ];

  const mockSystemStatus = {
    connectionStatus: 'Connected',
    lastSyncTime: '2024-12-19T15:30:00',
    pendingTransactions: 2,
    successfulToday: 5,
    errorsToday: 1,
    systemVersion: 'v2.4.1',
    maintenanceScheduled: '2024-12-20T02:00:00'
  };

  const mockErrorLogs = [
    {
      id: 1,
      timestamp: '2024-12-19T11:20:00',
      fileId: 4,
      errorType: 'Validation Error',
      message: 'Invalid bank code format in investor data - row 45',
      severity: 'High',
      resolved: false
    },
    {
      id: 2,
      timestamp: '2024-12-18T14:15:00',
      fileId: null,
      errorType: 'Connection Error',
      message: 'Temporary connection timeout to RENTAS server',
      severity: 'Medium',
      resolved: true,
      resolvedDate: '2024-12-18T14:30:00'
    }
  ];

  const getStatusBadge = (status) => {
    const statusConfig = {
      'Generated': { color: 'blue', icon: FileText },
      'Submitted': { color: 'yellow', icon: Clock },
      'Confirmed': { color: 'green', icon: CheckCircle },
      'Error': { color: 'red', icon: XCircle }
    };

    const config = statusConfig[status] || { color: 'gray', icon: Clock };
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${config.color}-100 text-${config.color}-800`}>
        <Icon className="w-3 h-3 mr-1" />
        {status}
      </span>
    );
  };

  const getSeverityColor = (severity) => {
    const colors = {
      'High': 'text-red-600',
      'Medium': 'text-yellow-600',
      'Low': 'text-blue-600'
    };
    return colors[severity] || 'text-gray-600';
  };

  const getFilteredFiles = () => {
    return mockRentasFiles.filter(file => {
      const matchesSearch = file.filename.toLowerCase().includes(searchTerm.toLowerCase()) ||
        file.bondName.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || file.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  };

  const handleRefreshStatus = () => {
    // Simulate system status refresh
    console.log('Refreshing RENTAS system status...');
  };

  const tabs = [
    { id: 'files', name: 'RENTAS Files', count: mockRentasFiles.length },
    { id: 'status', name: 'System Status', count: null },
    { id: 'errors', name: 'Error Logs', count: mockErrorLogs.filter(e => !e.resolved).length }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Payment File Generator</h1>
              <p className="mt-2 text-sm text-gray-600">Manage payment file generation and RENTAS system connectivity</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setSelectedTab(tab.id)}
                  className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${selectedTab === tab.id
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                >
                  {tab.name}
                  {tab.count !== null && tab.count > 0 && (
                    <span className={`ml-2 py-0.5 px-2 rounded-full text-xs ${selectedTab === tab.id ? 'bg-primary-100 text-primary-600' : 'bg-gray-100 text-gray-600'
                      }`}>
                      {tab.count}
                    </span>
                  )}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* RENTAS Files Tab */}
        {selectedTab === 'files' && (
          <>
            {/* Filters */}
            <div className="bg-white shadow rounded-lg mb-6">
              <div className="px-4 py-5 sm:p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Search */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Search Files
                    </label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search by filename or bond name..."
                        className="pl-10 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                  </div>

                  {/* Status Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Status
                    </label>
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="all">All Status</option>
                      <option value="Generated">Generated</option>
                      <option value="Submitted">Submitted</option>
                      <option value="Confirmed">Confirmed</option>
                      <option value="Error">Error</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* RENTAS Files List */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    RENTAS File Generation Log ({getFilteredFiles().length})
                  </h3>
                </div>

                <div className="space-y-4">
                  {getFilteredFiles().map((file) => (
                    <div key={file.id} className="border border-gray-200 rounded-lg p-6 hover:bg-gray-50">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="text-lg font-medium text-gray-900">
                              {file.filename}
                            </h4>
                            {getStatusBadge(file.status)}
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                            <div>
                              <p className="text-sm text-gray-500">Bond Name</p>
                              <p className="text-sm font-medium text-gray-900 mt-1">{file.bondName}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-500">Payment Date</p>
                              <div className="flex items-center mt-1">
                                <Calendar className="h-4 w-4 text-gray-400 mr-1" />
                                <p className="text-sm font-medium text-gray-900">{formatDate(file.paymentDate)}</p>
                              </div>
                            </div>
                            <div>
                              <p className="text-sm text-gray-500">Total Amount</p>
                              <div className="flex items-center mt-1">
                                <DollarSign className="h-4 w-4 text-gray-400 mr-1" />
                                <p className="text-sm font-medium text-gray-900">{formatCurrency(file.totalAmount)}</p>
                              </div>
                            </div>
                            <div>
                              <p className="text-sm text-gray-500">Generated</p>
                              <p className="text-sm font-medium text-gray-900 mt-1">{formatDate(file.generationDate)}</p>
                            </div>
                          </div>

                          <div className="mb-4">
                            <p className="text-sm text-gray-500">
                              {file.investorCount} investors • File size: {file.fileSize}
                            </p>
                          </div>

                          {/* Status Information */}
                          {file.status === 'Submitted' && file.submissionDate && (
                            <div className="mb-4 p-3 bg-yellow-50 rounded">
                              <p className="text-sm text-yellow-800">
                                Submitted to RENTAS on {formatDate(file.submissionDate)}
                              </p>
                            </div>
                          )}

                          {file.status === 'Confirmed' && file.confirmationDate && (
                            <div className="mb-4 p-3 bg-green-50 rounded">
                              <p className="text-sm text-green-800">
                                Confirmed by RENTAS on {formatDate(file.confirmationDate)}
                              </p>
                            </div>
                          )}

                          {file.status === 'Error' && file.errorMessage && (
                            <div className="mb-4 p-3 bg-red-50 rounded">
                              <p className="text-sm text-red-800">
                                Error: {file.errorMessage}
                              </p>
                            </div>
                          )}

                          {/* Action Buttons */}
                          <div className="flex flex-wrap gap-2">
                            <button
                              onClick={() => {
                                setSelectedFile(file);
                                setShowDetailModal(true);
                              }}
                              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              View Details
                            </button>

                            <a
                              href={file.downloadUrl}
                              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                            >
                              <Download className="h-4 w-4 mr-1" />
                              Download File
                            </a>

                            {file.confirmationFile && (
                              <a
                                href="#"
                                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                              >
                                <FileText className="h-4 w-4 mr-1" />
                                Download Confirmation
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                  {getFilteredFiles().length === 0 && (
                    <div className="text-center py-12">
                      <FileText className="mx-auto h-12 w-12 text-gray-400" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900">No files found</h3>
                      <p className="mt-1 text-sm text-gray-500">
                        No RENTAS files match your current filter criteria.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        {/* System Status Tab */}
        {selectedTab === 'status' && (
          <div className="space-y-6">
            {/* Integration Status Dashboard */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Integration Status</h3>
                  <button
                    onClick={handleRefreshStatus}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <RefreshCw className="h-4 w-4 mr-1" />
                    Refresh
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-green-50 p-4 rounded-lg">
                    <div className="flex items-center">
                      <CheckCircle className="h-8 w-8 text-green-600" />
                      <div className="ml-3">
                        <h4 className="text-sm font-medium text-green-800">Connection Status</h4>
                        <p className="text-lg font-bold text-green-900">{mockSystemStatus.connectionStatus}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-blue-50 p-4 rounded-lg">
                    <div className="flex items-center">
                      <Activity className="h-8 w-8 text-blue-600" />
                      <div className="ml-3">
                        <h4 className="text-sm font-medium text-blue-800">Pending Transactions</h4>
                        <p className="text-lg font-bold text-blue-900">{mockSystemStatus.pendingTransactions}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center">
                      <Settings className="h-8 w-8 text-gray-600" />
                      <div className="ml-3">
                        <h4 className="text-sm font-medium text-gray-800">System Version</h4>
                        <p className="text-lg font-bold text-gray-900">{mockSystemStatus.systemVersion}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-6">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Today's Activity</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-gray-50 p-3 rounded">
                      <p className="text-sm text-gray-500">Successful Transactions</p>
                      <p className="text-xl font-bold text-green-600">{mockSystemStatus.successfulToday}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded">
                      <p className="text-sm text-gray-500">Errors</p>
                      <p className="text-xl font-bold text-red-600">{mockSystemStatus.errorsToday}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded">
                      <p className="text-sm text-gray-500">Last Sync</p>
                      <p className="text-sm font-medium text-gray-900">{formatDate(mockSystemStatus.lastSyncTime)}</p>
                    </div>
                  </div>
                </div>

                {mockSystemStatus.maintenanceScheduled && (
                  <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex">
                      <AlertTriangle className="h-5 w-5 text-yellow-400" />
                      <div className="ml-3">
                        <h4 className="text-sm font-medium text-yellow-800">Scheduled Maintenance</h4>
                        <p className="text-sm text-yellow-700 mt-1">
                          RENTAS system maintenance is scheduled for {formatDate(mockSystemStatus.maintenanceScheduled)}.
                          Please complete all transactions before this time.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Error Logs Tab */}
        {selectedTab === 'errors' && (
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Error Logs</h3>
              </div>

              <div className="space-y-4">
                {mockErrorLogs.map((error) => (
                  <div key={error.id} className={`border rounded-lg p-4 ${error.resolved ? 'bg-gray-50' : 'bg-red-50'}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className={`text-sm font-medium ${getSeverityColor(error.severity)}`}>
                            {error.errorType}
                          </span>
                          <span className={`px-2 py-1 rounded text-xs ${error.resolved ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                            {error.resolved ? 'Resolved' : 'Open'}
                          </span>
                        </div>
                        <p className="text-sm text-gray-900 mb-2">{error.message}</p>
                        <div className="text-xs text-gray-500">
                          <span>Occurred: {formatDate(error.timestamp)}</span>
                          {error.resolved && error.resolvedDate && (
                            <span className="ml-4">Resolved: {formatDate(error.resolvedDate)}</span>
                          )}
                        </div>
                      </div>
                      <div className="ml-4">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${error.severity === 'High' ? 'bg-red-100 text-red-800' :
                            error.severity === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-blue-100 text-blue-800'
                          }`}>
                          {error.severity}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}

                {mockErrorLogs.length === 0 && (
                  <div className="text-center py-12">
                    <CheckCircle className="mx-auto h-12 w-12 text-green-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No errors</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      All systems are running smoothly.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* File Detail Modal */}
      {showDetailModal && selectedFile && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">RENTAS File Details</h3>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-2">File Information</h4>
                  <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <dt className="text-xs text-gray-500">Filename</dt>
                      <dd className="text-sm text-gray-900">{selectedFile.filename}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-gray-500">Bond Name</dt>
                      <dd className="text-sm text-gray-900">{selectedFile.bondName}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-gray-500">Payment Date</dt>
                      <dd className="text-sm text-gray-900">{formatDate(selectedFile.paymentDate)}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-gray-500">Generation Date</dt>
                      <dd className="text-sm text-gray-900">{formatDate(selectedFile.generationDate)}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-gray-500">Total Amount</dt>
                      <dd className="text-sm text-gray-900">{formatCurrency(selectedFile.totalAmount)}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-gray-500">Investor Count</dt>
                      <dd className="text-sm text-gray-900">{selectedFile.investorCount}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-gray-500">File Size</dt>
                      <dd className="text-sm text-gray-900">{selectedFile.fileSize}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-gray-500">Status</dt>
                      <dd className="text-sm text-gray-900">{getStatusBadge(selectedFile.status)}</dd>
                    </div>
                  </dl>
                </div>

                {selectedFile.submissionDate && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-2">Submission Information</h4>
                    <p className="text-sm text-gray-600">
                      Submitted to RENTAS on {formatDate(selectedFile.submissionDate)}
                    </p>
                  </div>
                )}

                {selectedFile.confirmationDate && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-2">Confirmation Information</h4>
                    <p className="text-sm text-gray-600">
                      Confirmed by RENTAS on {formatDate(selectedFile.confirmationDate)}
                    </p>
                  </div>
                )}

                {selectedFile.errorMessage && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-2">Error Information</h4>
                    <div className="p-3 bg-red-50 rounded border-l-4 border-red-400">
                      <p className="text-sm text-red-700">{selectedFile.errorMessage}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}


    </div>
  );
};

export default RentasIntegration; 